const DEFAULT_MARKET_COUNTRY_CODE = 'SE'

const marketMetadataByCountryCode = {
	SE: {
		currency: {
			code: 'SEK',
			symbol: 'kr',
			fractionDigits: 0,
			mcuExponent: 2,
			separator: '.',
			pattern: '# !',
		},
		language: 'en-US',
		timezone: 'Europe/Stockholm',
		payment: {
			budgetMinimum: 3,
			budgetMinimumMessage: 'A budget of at least 3 kr is required to proceed',
			budgetPrecision: 0,
			publishableKeyConfigKey: 'publishableKeySe',
			stripeLiveProvider: 'StripeIntentSE',
			stripeTestProvider: 'StripeIntentDev',
			supported: true,
			vatRate: 0.25,
		},
		urlSelectable: true,
	},
	FI: {
		currency: {
			code: 'EUR',
			symbol: '€',
			fractionDigits: 2,
			separator: ' ',
			pattern: '! #',
		},
		language: 'fi-FI',
		timezone: 'UTC',
		payment: {
			budgetMinimum: 0.5,
			budgetMinimumMessage: 'A budget of at least € 0.5 is required to proceed',
			budgetPrecision: 2,
			publishableKeyConfigKey: 'publishableKeyFi',
			stripeLiveProvider: 'StripeIntentFI',
			stripeTestProvider: 'StripeIntentDev',
			supported: true,
			vatRate: 0.24,
		},
		urlSelectable: true,
	},
	LU: {
		currency: {
			code: 'EUR',
			symbol: '€',
			fractionDigits: 2,
			separator: ' ',
			pattern: '! #',
		},
		language: 'fr-LU',
		timezone: 'Europe/Luxembourg',
		payment: {
			supported: false,
			vatRate: 0,
		},
		urlSelectable: true,
	},
	RO: {
		currency: {
			code: 'RON',
			symbol: 'RON',
			fractionDigits: 2,
			separator: ' ',
			pattern: '# !',
		},
		timezone: 'UTC',
		urlSelectable: false,
	},
	CZ: {
		currency: {
			code: 'CZK',
			symbol: 'CZK',
			fractionDigits: 2,
			separator: ' ',
			pattern: '# !',
		},
		timezone: 'UTC',
		urlSelectable: false,
	},
	PE: {
		currency: {
			code: 'PEN',
			symbol: 'S/',
			fractionDigits: 2,
			separator: ' ',
			pattern: '! #',
		},
		language: 'es-PE',
		timezone: 'America/Lima',
		urlSelectable: false,
	},
}

const additionalCurrencyMetadataByCode = {
	XTS: {
		code: 'XTS',
		fractionDigits: 3,
		pattern: '# !',
		separator: ' ',
		symbol: '💰',
	},
}

const normalizeCountryCode = countryCode => String(countryCode || '').trim().toUpperCase()
const normalizeCurrencyCode = currencyCode => String(currencyCode || '').trim().toUpperCase()

const getRequiredMarketConfig = countryCode => {
	const normalizedCountryCode = normalizeCountryCode(countryCode)
	const marketConfig = marketMetadataByCountryCode[normalizedCountryCode]

	if (!marketConfig) {
		throw new Error(`Unknown countryCode: ${countryCode}`)
	}

	return marketConfig
}

const getCurrencyMetadataByCode = currencyCode => {
	const normalizedCurrencyCode = normalizeCurrencyCode(currencyCode)

	const marketCurrency = Object.values(marketMetadataByCountryCode)
		.map(({ currency }) => currency)
		.find(currency => currency.code === normalizedCurrencyCode)

	if (marketCurrency) {
		return marketCurrency
	}

	const additionalCurrency = additionalCurrencyMetadataByCode[normalizedCurrencyCode]
	if (additionalCurrency) {
		return additionalCurrency
	}

	throw new Error(`Unknown currency: ${currencyCode}`)
}

export const getDefaultMarketCountryCode = () => DEFAULT_MARKET_COUNTRY_CODE

export const isKnownMarket = countryCode => Boolean(marketMetadataByCountryCode[normalizeCountryCode(countryCode)])

export const getMarketConfig = countryCode => getRequiredMarketConfig(countryCode)

export const isUrlSelectableMarket = countryCode => {
	const marketConfig = marketMetadataByCountryCode[normalizeCountryCode(countryCode)]
	return Boolean(marketConfig?.urlSelectable)
}

export const isPaymentSupportedMarket = countryCode => {
	const marketConfig = marketMetadataByCountryCode[normalizeCountryCode(countryCode)]
	return Boolean(marketConfig?.payment?.supported)
}

export const getMarketCurrency = countryCode => getMarketConfig(countryCode).currency.code

export const getMarketCurrencySymbol = countryCode => getMarketConfig(countryCode).currency.symbol

export const getMarketTimezone = countryCode => getMarketConfig(countryCode).timezone || 'UTC'

export const getMarketLanguage = countryCode => {
	const language = getMarketConfig(countryCode).language

	if (!language) {
		throw new Error(`Unknown countryCode: ${countryCode}`)
	}

	return language
}

export const getMarketVatRate = countryCode => {
	const vatRate = getMarketConfig(countryCode).payment?.vatRate

	if (vatRate === undefined) {
		throw new Error(`Unknown countryCode: ${countryCode}`)
	}

	return vatRate
}

export const getMarketAmountFormat = countryCode => ({
	decimal: ',',
	separator: getMarketConfig(countryCode).currency.separator,
	pattern: getMarketConfig(countryCode).currency.pattern,
})

export const getMarketPaymentConfig = countryCode => {
	const paymentConfig = getMarketConfig(countryCode).payment

	if (!paymentConfig) {
		throw new Error(`Unknown countryCode: ${countryCode}`)
	}

	return paymentConfig
}

export const getCurrencySymbol = currencyCode => getCurrencyMetadataByCode(currencyCode).symbol

export const getCurrencyFractionDigits = currencyCode => getCurrencyMetadataByCode(currencyCode).fractionDigits

export const getCurrencyMcuExponent = currencyCode => {
	const currency = getCurrencyMetadataByCode(currencyCode)
	return currency.mcuExponent ?? currency.fractionDigits
}

export const getCurrencyPattern = currencyCode => getCurrencyMetadataByCode(currencyCode).pattern

export const getCurrencySeparator = currencyCode => getCurrencyMetadataByCode(currencyCode).separator

export const getKnownMarketCurrencyCodes = () =>
	Array.from(new Set(Object.values(marketMetadataByCountryCode).map(({ currency }) => currency.code)))

export const getUrlSelectableMarketCodes = () =>
	Object.keys(marketMetadataByCountryCode)
		.filter(countryCode => marketMetadataByCountryCode[countryCode].urlSelectable)
		.sort((left, right) => left.localeCompare(right))
